/**
 * Creation Provenance — two orthogonal axes
 *
 * 1. Source — who/what authored the content
 *    human | ai | automated
 *
 * 2. Review — whether a person has endorsed it (optional; never required)
 *    none | human_reviewed
 *
 * Do not compress these into one toggle. AI-generated content may remain
 * unreviewed forever; automated (non-AI) systems can also be reviewed.
 *
 * Legacy compressed encoding (still on some columns / filters):
 *   human       ↔ source=human, review=none
 *   ai_draft    ↔ source=ai, review=none
 *   ai_approved ↔ source=ai, review=human_reviewed
 *
 * See: docs/1-canon/30-creation-provenance.md
 */

/** Who/what authored the content. */
export type CreationSource = 'human' | 'ai' | 'automated';

/** Whether a human has endorsed the content. Independent of source. */
export type CreationReviewStatus = 'none' | 'human_reviewed';

export interface CreationAuthorship {
  source: CreationSource;
  reviewStatus: CreationReviewStatus;
}

/** @deprecated Compressed legacy enum — prefer CreationAuthorship. */
export type CreationProvenance = 'human' | 'ai_draft' | 'ai_approved';

export const CREATION_SOURCE_LABELS: Record<CreationSource, string> = {
  human: 'Human',
  ai: 'AI-generated',
  automated: 'Automated',
};

export const CREATION_SOURCE_DESCRIPTIONS: Record<CreationSource, string> = {
  human: 'Created by a human.',
  ai: 'Generated by AI. Review is optional — this may remain unreviewed.',
  automated: 'Generated by an automated (non-AI) system. Review is optional.',
};

export const CREATION_REVIEW_LABELS: Record<CreationReviewStatus, string> = {
  none: 'Not reviewed',
  human_reviewed: 'Human-reviewed',
};

export const CREATION_REVIEW_DESCRIPTIONS: Record<CreationReviewStatus, string> = {
  none: 'No human endorsement recorded.',
  human_reviewed: 'A person has reviewed and endorsed this content.',
};

/** Legacy labels kept for filters / older call sites during transition. */
export const CREATION_PROVENANCE_LABELS: Record<CreationProvenance, string> = {
  human: 'Human',
  ai_draft: 'AI-generated',
  ai_approved: 'AI-generated · Human-reviewed',
};

export const CREATION_PROVENANCE_DESCRIPTIONS: Record<CreationProvenance, string> = {
  human: 'Created by a human.',
  ai_draft: 'Generated by AI. Not human-reviewed (review is optional).',
  ai_approved: 'Generated by AI and human-reviewed.',
};

export const CREATION_PROVENANCE_FILTER_OPTIONS: Array<{
  value: CreationProvenance;
  label: string;
}> = [
  { value: 'human', label: CREATION_PROVENANCE_LABELS.human },
  { value: 'ai_draft', label: CREATION_PROVENANCE_LABELS.ai_draft },
  { value: 'ai_approved', label: CREATION_PROVENANCE_LABELS.ai_approved },
];

export function authorshipFromLegacy(provenance: CreationProvenance): CreationAuthorship {
  switch (provenance) {
    case 'ai_draft':
      return { source: 'ai', reviewStatus: 'none' };
    case 'ai_approved':
      return { source: 'ai', reviewStatus: 'human_reviewed' };
    case 'human':
    default:
      return { source: 'human', reviewStatus: 'none' };
  }
}

/** Compress two axes into the legacy column value (for back-compat writers). */
export function legacyFromAuthorship(authorship: CreationAuthorship): CreationProvenance {
  if (authorship.source === 'human') return 'human';
  if (authorship.reviewStatus === 'human_reviewed') return 'ai_approved';
  // ai or automated, unreviewed — legacy column has no automated; map both to ai_draft
  return 'ai_draft';
}

export function resolveAuthorship(input: {
  source?: CreationSource | null;
  reviewStatus?: CreationReviewStatus | null;
  /** Legacy compressed field */
  provenance?: CreationProvenance | string | null;
}): CreationAuthorship {
  if (input.source === 'human' || input.source === 'ai' || input.source === 'automated') {
    const reviewStatus =
      input.reviewStatus === 'human_reviewed' ? 'human_reviewed' : 'none';
    return { source: input.source, reviewStatus };
  }
  if (
    input.provenance === 'human' ||
    input.provenance === 'ai_draft' ||
    input.provenance === 'ai_approved'
  ) {
    return authorshipFromLegacy(input.provenance);
  }
  return { source: 'human', reviewStatus: 'none' };
}

/**
 * Whether a list-style surface shows an authorship mark beside this thing.
 *
 * Owner rule (2026-10-13): a mark appears only while the thing is AI-generated
 * (or automated) and not yet confirmed by a person. AI-suggested then
 * human-confirmed, and human-entered, show nothing beside them. Detail
 * surfaces keep the full two-axis badge (canon doc 30); this rule is for
 * rows, cards, and pickers where the mark is a signal, not a record.
 */
export function authorshipNeedsMark(input: {
  source?: CreationSource | null;
  reviewStatus?: CreationReviewStatus | null;
  /** Legacy compressed field */
  provenance?: CreationProvenance | string | null;
}): boolean {
  const authorship = resolveAuthorship(input);
  return authorship.source !== 'human' && authorship.reviewStatus !== 'human_reviewed';
}
